import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, FileText, MapPin } from 'lucide-react';
import RoutePlaybackMap from '../maps/RoutePlaybackMap';
import CommandCenterTripLiveMap from './CommandCenterTripLiveMap';
import SlaTimer, { getActiveStageTimerConfig } from './SlaTimer';
import { useBookingWebSocket } from '../../hooks/useWebSocket';
import webSocketService from '../../services/webSocketService';
import commandCenterService from '../../services/commandCenterService';
import PatientLocationSharePopup from './PatientLocationSharePopup';
import { usePatientLocationShare, bookingPatientPhone } from '../../hooks/usePatientLocationShare';

// Real live driver position — same pattern as the Hospital dashboard's
// TrackingMapWrapper (HospitalTrackingScreen.jsx): join the booking's
// Socket.IO room and listen for the backend's real driver_location_updated
// broadcast (driverAppService.updateDriverLocation → broadcastToBooking),
// which fires for any active booking regardless of which module created it.
// Seeds from the last-known snapshot on the booking (trip.driver.location)
// so the marker isn't empty before the first live ping arrives.
const useLiveDriverPosition = (bookingId, snapshot, enabled) => {
  const [liveLocation, setLiveLocation] = useState(null);
  useBookingWebSocket(enabled ? bookingId : null);

  useEffect(() => {
    setLiveLocation(null);
    if (!enabled || !bookingId) return undefined;

    const handleDriverLocationUpdated = (data) => {
      if (data.bookingId !== bookingId && data.booking_id !== bookingId) return;
      const loc = data.tracking?.driverLocation;
      if (!loc) return;
      const lat = parseFloat(loc.latitude ?? loc.lat);
      const lng = parseFloat(loc.longitude ?? loc.lng);
      if (!isFinite(lat) || !isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
      setLiveLocation({
        latitude: lat,
        longitude: lng,
        lastUpdated: new Date().toISOString(),
        heading: loc.heading ?? loc.bearing ?? null,
        speed: loc.speed ?? null,
      });
    };

    webSocketService.on('driver_location_updated', handleDriverLocationUpdated);
    return () => webSocketService.off('driver_location_updated', handleDriverLocationUpdated);
  }, [bookingId, enabled]);

  return liveLocation || snapshot || null;
};

// Real driven-route history for the Completed stage — same DriverLocationHistory
// / BookingTrackingEvents data (and RoutePlaybackMap component) the Hospital
// dashboard's own history page uses, via Command Center's own tracking
// endpoints (commandCenterDispatchService.getBookingRoute/getBookingTrackingEvents).
// Off-platform (Add & Assign / call-notified) bookings never had a driver app
// pinging location, so this legitimately comes back empty for those — that's
// surfaced as a note, not an error.
const useCompletedRouteHistory = (bookingId, enabled) => {
  const [routeData, setRouteData] = useState([]);
  const [trackingEvents, setTrackingEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refetch = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    setRouteData([]);
    setTrackingEvents([]);
    if (!enabled || !bookingId) return undefined;

    let cancelled = false;
    setLoading(true);
    Promise.all([
      commandCenterService.getBookingRoute(bookingId),
      commandCenterService.getBookingTrackingEvents(bookingId),
    ])
      .then(([routeResult, eventsResult]) => {
        if (cancelled) return;
        const parseArr = (res) => {
          if (!res) return [];
          if (Array.isArray(res)) return res;
          if (Array.isArray(res.data)) return res.data;
          if (Array.isArray(res.data?.data)) return res.data.data;
          return [];
        };
        setRouteData(parseArr(routeResult));
        setTrackingEvents(parseArr(eventsResult));
      })
      .catch(() => {
        if (!cancelled) { setRouteData([]); setTrackingEvents([]); }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [bookingId, enabled, refreshTrigger]);

  return { routeData, trackingEvents, loading, refetch };
};

const STAGE_MAP_NOTE = {
  'Requested': 'Pickup and drop-off locations — requested from the B2C app, awaiting dispatch.',
  'Pending': 'Pickup and drop-off locations — no driver assigned yet.',
  'Scheduled': 'Pickup and drop-off locations, plus live driver location if one is already assigned.',
  'Active Trips': 'Live tracking and route for ongoing trip.',
  'Towards Pickup': 'Live driver location heading to pickup.',
  'At Pickup': 'Driver is stationary at the pickup location.',
  'Towards Drop': 'Live driver location heading to drop-off.',
  'At Drop': 'Driver is stationary at the drop-off location.',
  'Untracked': 'Driver assigned outside the driver app (SMS/manual) — no GPS feed available, showing pickup and drop-off only.',
  'Completed': "Full driven route with real GPS location history, like the Hospital Dashboard's history page.",
  'Cancelled': 'Pickup and drop-off locations for the cancelled trip.',
  'Rejected': 'Pickup and drop-off locations for the rejected trip.',
};

// Stages where a real, app-tracked driver's live position is meaningful to
// show. Untracked (off-platform) bookings have no GPS feed at all, and
// terminal stages don't have a moving driver anymore. At Pickup/At Drop are
// included too — the driver isn't moving, but their live marker (stationary)
// is still meaningful to show rather than blank.
const LIVE_TRACKING_STAGES = ['Scheduled', 'Active Trips', 'Towards Pickup', 'At Pickup', 'Towards Drop', 'At Drop'];

const CommandCenterTripMap = ({ trip, onClose, onOpenDetails }) => {
  const parseCoord = (val) => {
    if (val == null) return null;
    const num = parseFloat(val);
    return isFinite(num) ? num : null;
  };

  const raw = trip?.rawBooking || {};
  const pLat = parseCoord(trip?.pickup?.latitude ?? trip?.pickup?.lat ?? raw.pickup_latitude);
  const pLng = parseCoord(trip?.pickup?.longitude ?? trip?.pickup?.lng ?? raw.pickup_longitude);
  const dLat = parseCoord(trip?.dropoff?.latitude ?? trip?.dropoff?.lat ?? raw.dropoff_latitude);
  const dLng = parseCoord(trip?.dropoff?.longitude ?? trip?.dropoff?.lng ?? raw.dropoff_longitude);

  const resolvedPickup = pLat != null && pLng != null ? {
    latitude: pLat,
    longitude: pLng,
    lat: pLat,
    lng: pLng,
    address: trip?.pickup?.address || raw.pickup_address,
  } : trip?.pickup;

  const resolvedDropoff = dLat != null && dLng != null ? {
    latitude: dLat,
    longitude: dLng,
    lat: dLat,
    lng: dLng,
    address: trip?.dropoff?.address || raw.dropoff_address,
  } : trip?.dropoff;

  const isLiveTrackable = !!trip && LIVE_TRACKING_STAGES.includes(trip.stage);
  const initialAmbulanceSnapshot =
    trip?.ambulanceLocation ||
    trip?.driver?.location ||
    (trip?.driver?.latitude != null ? { latitude: trip.driver.latitude, longitude: trip.driver.longitude } : null) ||
    (raw?.vehicle?.current_latitude != null
      ? { latitude: parseFloat(raw.vehicle.current_latitude), longitude: parseFloat(raw.vehicle.current_longitude) }
      : null);

  const liveAmbulanceLocation = useLiveDriverPosition(trip?.id, initialAmbulanceSnapshot, isLiveTrackable);

  const fallbackMidpoint =
    pLat != null && dLat != null && pLng != null && dLng != null
      ? { latitude: (pLat + dLat) / 2, longitude: (pLng + dLng) / 2, lat: (pLat + dLat) / 2, lng: (pLng + dLng) / 2 }
      : null;

  const resolvedAmbulanceLocation =
    liveAmbulanceLocation ||
    initialAmbulanceSnapshot ||
    (trip?.driver ? fallbackMidpoint : null);

  const isCompleted = trip?.stage === 'Completed';
  const { routeData: completedRouteData, trackingEvents: completedTrackingEvents, loading: loadingHistory, refetch: refetchHistory } =
    useCompletedRouteHistory(trip?.id, isCompleted);

  // Where the patient says they are (via the SMS / WhatsApp link) - drawn as an extra marker, pickup is untouched
  const { patientLocation, share: locationShare, refresh: refreshLocationShare } = usePatientLocationShare(trip?.id, Boolean(trip));
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const canRequestLocation = Boolean(trip) && !['Completed', 'Cancelled'].includes(trip.stage);

  if (!trip) return null;

  const hasNoLocationData = !resolvedPickup && !resolvedDropoff;

  const renderMap = () => {
    if (hasNoLocationData) {
      return (
        <div className="w-full h-full flex items-center justify-center text-center px-8 bg-gray-50">
          <p className="text-sm text-gray-400">
            No pickup/drop-off coordinates recorded for this booking — it was likely created without a map-picked location.
          </p>
        </div>
      );
    }

    if (trip.stage === 'Completed') {
      if (loadingHistory) {
        return (
          <div className="w-full h-full flex items-center justify-center gap-2 text-sm text-gray-400 bg-gray-50">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading trip history…
          </div>
        );
      }
      return (
        <RoutePlaybackMap
          key={trip.id}
          routeData={completedRouteData}
          trackingEvents={completedTrackingEvents}
          pickupLocation={resolvedPickup}
          dropoffLocation={resolvedDropoff}
          pickupAddress={resolvedPickup?.address || trip.pickupAddress || trip.pickup_address}
          dropoffAddress={resolvedDropoff?.address || trip.dropoffAddress || trip.dropoff_address || trip.hospital?.name || trip.hospitalName}
          bookingStatus="COMPLETED"
          hidePickupDropoff={false}
          showControls={true}
          zoom={14}
          height="100%"
          onRefresh={refetchHistory}
          startTime={trip.when?.at || trip.createdAt || raw.created_at}
          endTime={trip.completedAt || raw.Trip?.end_time || raw.updated_at}
          durationMinutes={trip.actualDurationMinutes ?? trip.duration?.actualMin ?? raw.actual_duration_minutes}
        />
      );
    }

    return (
      <CommandCenterTripLiveMap
        key={trip.id}
        pickupLocation={resolvedPickup}
        dropoffLocation={resolvedDropoff}
        ambulanceLocation={resolvedAmbulanceLocation}
        patientLocation={patientLocation}
        stage={trip.stage}
        height="100%"
      />
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 md:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] max-h-[920px] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0 bg-white">
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2 flex-wrap">
              Trip Map <span className="text-blue-600 font-bold">#{trip.id}</span>
              {(() => {
                const stageTimer = getActiveStageTimerConfig(trip);
                if (!stageTimer) return null;
                return (
                  <div className="flex items-center gap-1 font-normal text-xs ml-2">
                    <span className="text-gray-500">{stageTimer.label}</span>
                    <SlaTimer
                      startAt={stageTimer.startAt}
                      warnAmberSeconds={stageTimer.warnAmberSeconds}
                      warnRedSeconds={stageTimer.warnRedSeconds}
                      label="Stage SLA"
                    />
                  </div>
                );
              })()}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {hasNoLocationData
                ? 'No coordinates recorded for this booking.'
                : isLiveTrackable && !resolvedAmbulanceLocation
                ? "Waiting for the driver's live location…"
                : isCompleted && !loadingHistory && completedRouteData.length === 0
                ? 'No GPS history recorded for this trip — likely completed off-platform (call/manual driver), showing route between pickup and drop-off.'
                : STAGE_MAP_NOTE[trip.stage]}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canRequestLocation && (
              <button
                type="button"
                onClick={() => setShowLocationPopup(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 text-xs font-medium transition-colors cursor-pointer shadow-2xs"
                title="Send the patient a link to share their location"
              >
                <MapPin className="w-3.5 h-3.5 text-green-600" />
                <span>{patientLocation ? 'Patient location' : locationShare ? 'Location link' : 'Request patient location'}</span>
              </button>
            )}
            {onOpenDetails && (
              <button
                type="button"
                onClick={() => onOpenDetails(trip)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 text-xs font-medium transition-colors cursor-pointer shadow-2xs"
                title="View full booking & dispatch details"
              >
                <FileText className="w-3.5 h-3.5 text-gray-500" />
                <span>Overview</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors shrink-0 cursor-pointer"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 relative overflow-hidden">
          {renderMap()}
        </div>
      </div>

      {showLocationPopup && (
        <PatientLocationSharePopup
          target="pickup"
          phone={bookingPatientPhone(raw)}
          patientName={raw.patient_name || trip.patientName}
          bookingId={trip.id}
          existingShareId={locationShare?.shareId}
          onClose={() => { setShowLocationPopup(false); refreshLocationShare(); }}
        />
      )}
    </div>,
    document.body
  );
};

export default CommandCenterTripMap;